import { useState } from "react";
import { ThumbsUp, ThumbsDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const OPTIONS = [
  { rating: "green", label: "Helpful", Icon: ThumbsUp, ring: "#16a34a", bg: "#dcfce7", fg: "#15803d" },
  { rating: "yellow", label: "Needs work", Icon: Minus, ring: "#f59e0b", bg: "#fef3c7", fg: "#b45309" },
  { rating: "red", label: "Not helpful", Icon: ThumbsDown, ring: "#dc2626", bg: "#fee2e2", fg: "#b91c1c" },
];

/** Traffic-light article feedback. `counts` (editors only) shows tallies. */
export function KbFeedback({ slug, initial, counts }) {
  const [rating, setRating] = useState(initial || null);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);

  const vote = async (r) => {
    if (saving) return;
    setSaving(true);
    setRating(r);
    try {
      await api.post(`/kb/articles/${slug}/feedback`, { rating: r, comment: comment.trim() || undefined });
      toast.success("Thanks for the feedback");
      if (r !== "green") setShowComment(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const sendComment = async () => {
    if (!rating || !comment.trim()) { setShowComment(false); return; }
    try {
      await api.post(`/kb/articles/${slug}/feedback`, { rating, comment: comment.trim() });
      toast.success("Comment saved");
      setShowComment(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <section className="mt-12 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 px-5 py-5" data-testid="kb-feedback">
      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Was this article helpful?</h2>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {OPTIONS.map(({ rating: r, label, Icon, ring, bg, fg }) => {
          const active = rating === r;
          return (
            <button key={r} data-testid={`kb-feedback-${r}`} disabled={saving} onClick={() => vote(r)}
              className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all disabled:opacity-60"
              style={active
                ? { borderColor: ring, background: bg, color: fg, boxShadow: `0 0 0 3px ${ring}22` }
                : { borderColor: "#e2e8f0", background: "#fff", color: "#475569" }}>
              <Icon className="h-4 w-4" style={{ color: active ? fg : ring }} />
              {label}
              {counts && <span className="ml-1 rounded-full bg-white/70 px-1.5 text-xs">{counts[r] ?? 0}</span>}
            </button>
          );
        })}
      </div>
      {showComment && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row" data-testid="kb-feedback-comment">
          <input value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="What could be better? (optional)"
            className="flex-1 rounded-md border border-slate-200 dark:border-slate-800 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
          <button onClick={sendComment} data-testid="kb-feedback-comment-send"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Send
          </button>
        </div>
      )}
    </section>
  );
}
